import { CircularProgress, Stack, styled } from '@mui/material'
import { TextButtonNew } from '../../buttons/TextButton/TextButtonNew'
import { TinyButton } from '../../buttons/TinyButton/TinyButton'
import { NumberInput } from '../../forms'
import { Typography } from '../../text'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { Checkbox } from '../../selectors'
import { ReactElement } from 'react'

export type ModalFooterProps = {
    primaryButton?: {
        text: string
        destructive?: boolean
        disabled?: boolean
        onClick: () => void
        icon?: ReactElement
        rightIcon?: ReactElement
    }
    secondaryButton?: {
        text: string
        disabled?: boolean
        onClick: () => void
        position?: 'left' | 'right'
        icon?: ReactElement
    }
    tertiaryButton?: {
        text: string
        disabled?: boolean
        onClick: () => void
    }
    destructiveButton?: {
        text: string
        disabled?: boolean
        onClick: () => void
    }
    bulkCreate?: {
        setValue: (value: number | undefined) => void
        value: number | undefined
        label: string
        disabled?: boolean
        errorMessage?: string
        min?: number
    }
    createMore?: {
        setIsChecked: (value: boolean) => void
        isChecked: boolean
        label: string
        disabled?: boolean
    }
    fullWidth?: boolean
    isPending?: boolean
}

export function ModalFooter({
    primaryButton,
    secondaryButton,
    tertiaryButton,
    destructiveButton,
    bulkCreate,
    createMore,
    isPending,
}: ModalFooterProps) {
    const showBulkCreate = !!bulkCreate
    const showCreateMore = !!createMore
    const showSecondaryButtonOnLeft = secondaryButton?.position === 'left'
    const showSecondaryButtonOnRight = !!secondaryButton && !showSecondaryButtonOnLeft

    const handleBulkCreateNumberChange = (value: number | undefined) => {
        if (value === undefined || (Number.isInteger(value) && value >= (bulkCreate?.min ?? 0) && value <= 99)) {
            bulkCreate?.setValue(value)
        }
    }

    const toggleCreateMore = () => {
        createMore?.setIsChecked(!createMore.isChecked)
    }

    return (
        <ModalFooterWrapper direction="row">
            <OptionalComponentsWrapper direction="row">
                {showSecondaryButtonOnLeft && (
                    <TextButtonNew
                        label={secondaryButton.text}
                        onClick={secondaryButton.onClick}
                        variant="secondary"
                        disabled={secondaryButton.disabled}
                        leftIcon={secondaryButton.icon}
                    />
                )}
                {destructiveButton && (
                    <TextButtonNew
                        label={destructiveButton.text}
                        onClick={destructiveButton.onClick}
                        variant="destructive"
                        disabled={destructiveButton.disabled}
                    />
                )}
                {showBulkCreate && (
                    <OptionWrapper direction="row">
                        <BulkCreateNumberInput
                            value={bulkCreate.value}
                            onValueChange={handleBulkCreateNumberChange}
                            aria-label={bulkCreate.label}
                            disabled={bulkCreate.disabled}
                            errorMessage={bulkCreate.errorMessage}
                            format="integer"
                        />
                        <Typography variant="textXS">{bulkCreate.label}</Typography>
                    </OptionWrapper>
                )}
                {showCreateMore && (
                    <OptionWrapper direction="row">
                        <Checkbox
                            onChange={() => toggleCreateMore()}
                            checked={createMore.isChecked}
                            ariaLabel={createMore.label}
                            disabled={createMore.disabled}
                        />
                        <Typography variant="textXS">{createMore.label}</Typography>
                    </OptionWrapper>
                )}
            </OptionalComponentsWrapper>
            <ButtonWrapper direction="row">
                {tertiaryButton && (
                    <TinyButton label={tertiaryButton.text} onClick={tertiaryButton.onClick} disabled={tertiaryButton.disabled} />
                )}
                {showSecondaryButtonOnRight && (
                    <TextButtonNew
                        label={secondaryButton.text}
                        onClick={secondaryButton.onClick}
                        variant="secondary"
                        disabled={secondaryButton.disabled}
                        leftIcon={secondaryButton.icon}
                    />
                )}
                {primaryButton && (
                    <TextButtonNew
                        label={primaryButton.text}
                        onClick={primaryButton.onClick}
                        variant={primaryButton.destructive ? 'destructive' : 'primary'}
                        disabled={primaryButton.disabled}
                        leftIcon={
                            isPending ? (
                                <CircularProgress
                                    thickness={4}
                                    size={tokens.SizingIconS}
                                    sx={{
                                        color: primaryButton.disabled ? tokens.ColorIconDisabled : tokens.ColorIconInverse,
                                        position: 'relative',
                                        left: 'initial',
                                        top: 'initial',
                                    }}
                                />
                            ) : (
                                primaryButton.icon
                            )
                        }
                        rightIcon={primaryButton.rightIcon}
                    />
                )}
            </ButtonWrapper>
        </ModalFooterWrapper>
    )
}

const ModalFooterWrapper = styled(Stack)`
    display: flex;
    width: 100%;
    padding: ${tokens.SizingSpacing200} ${tokens.SizingSpacing300};
    align-items: center;
    justify-content: space-between;
    border-top: ${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault};
    background: ${tokens.ColorSurfaceDefault};
    box-sizing: border-box;
`

const ButtonWrapper = styled(Stack)`
    display: flex;
    gap: ${tokens.SizingSpacing150};
    box-sizing: content-box;
`

const OptionalComponentsWrapper = styled(Stack)`
    display: flex;
    justify-content: center;
    gap: ${tokens.SizingSpacing150};
`

const OptionWrapper = styled(Stack)`
    display: flex;
    align-items: center;
    gap: ${tokens.SizingSpacing150};
`

const BulkCreateNumberInput = styled(NumberInput)`
    && {
        min-height: ${tokens.SizingSpacing500};
        width: ${tokens.SizingSpacing500};
    }

    && .MuiOutlinedInput-root {
        height: ${tokens.SizingSpacing500};
        padding: 0 ${tokens.SizingSpacing100};
    }

    && .MuiOutlinedInput-input {
        text-align: center;
    }
`
